import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0", {
  variants: {
    variant: {
      default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]",
      destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:scale-[1.02] active:scale-[0.98]",
      outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent hover:scale-[1.02] active:scale-[0.98]",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:scale-[1.02] active:scale-[0.98]",
      ghost: "hover:bg-accent hover:text-accent-foreground",
      link: "text-primary underline-offset-4 hover:underline",
      // CTA Principal - Gradiente violeta con texto blanco
      gold: "bg-gradient-to-r from-[#5A4A8A] to-[#8B7BB5] text-white font-bold tracking-wide shadow-lg hover:shadow-[0_12px_40px_rgba(107,91,154,0.5)] hover:scale-[1.03] hover:from-[#6B5B9A] hover:to-[#9B8AC4] active:scale-[0.98] border border-[#C4A8E0]/30",
      // CTA Secundario - Borde lavanda con texto blanco
      goldOutline: "border-2 border-[#C4A8E0] text-white bg-white/10 backdrop-blur-sm font-semibold hover:bg-[#8B7BB5] hover:border-[#8B7BB5] hover:text-white hover:scale-[1.02] hover:shadow-[0_8px_30px_rgba(139,123,181,0.4)] active:scale-[0.98]",
      // Premium - Gradiente suave lavanda
      premium: "relative overflow-hidden bg-gradient-to-r from-[#6B5B9A]/80 to-[#C4A8E0]/60 text-white border border-[#C4A8E0]/50 hover:border-[#C4A8E0] hover:from-[#8B7BB5] hover:to-[#C4A8E0]/80 hover:shadow-[0_8px_30px_rgba(196,168,224,0.4)] hover:scale-[1.02] font-medium active:scale-[0.98]"
    },
    size: {
      default: "h-10 px-4 py-2",
      sm: "h-9 rounded-md px-3",
      lg: "h-11 rounded-md px-8",
      xl: "h-14 rounded-lg px-10 text-base",
      icon: "h-10 w-10"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
});
Button.displayName = "Button";
export { Button, buttonVariants };